import clsx from 'clsx';
import { InputField } from 'common';
import { Icon } from 'common/icon';
import { Button } from 'common/new-button';
import { useAlert } from 'components';
import { ICasino, ISlot } from 'constants/types';
import { Formik, FormikValues } from 'formik';
import Image from 'next/image';
import { initialValues, model, validationSchema } from './config';

import { Dropdown, DropdownRef } from 'components/dropdown/slots';
import { DropdownItem } from 'components/dropdown/slots/item';

import { useCallback, useMemo, useRef, useState } from 'react';
import { useCreateSlotReviewMutation } from 'hooks/api/slot_reviews';
import { useMeQuery } from 'hooks/api/users';
import { handleMutation } from 'utils/handleMutation';
import styles from './styles.module.css';

import { LogoOverlay, LogoOverlayWrapper } from 'components/logo-overlay';

import { SelectArrow } from 'components/modal-arrow';
import { useClickOutside } from 'hooks/useClickOutside';
import { useCasinosBySlotQuery } from 'hooks/api/casino';
import { crocodileSearch } from 'utils/search';

type Props = {
  slot?: ISlot;
  isVisible: boolean;
  setVisible: (isVisible: boolean) => void;
};

const DropDownButton = ({
  isOpen,
  onClick,
  searchQuery,
  selectedSlot,
  setSearchQuery,
}: {
  isOpen?: boolean;
  onClick?: () => void;
  searchQuery: string;
  selectedSlot: ICasino | null;
  setSearchQuery: (query: string) => void;
}) => {
  const handleButtonClick = () => {
    if (onClick) {
      onClick();
    }
  };

  if (isOpen) {
    return (
      <div className={styles.slot_row} onClick={handleButtonClick}>
        <input
          autoFocus
          value={searchQuery}
          placeholder={'Search casino'}
          className={styles.slot_search_input}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <SelectArrow isOpen={isOpen} />
      </div>
    );
  }

  if (!selectedSlot) {
    return (
      <div className={styles.slot_row} onClick={handleButtonClick}>
        <span className={styles.slot_name}>{'Select casino'}</span>
        <SelectArrow isOpen={isOpen} />
      </div>
    );
  }

  return (
    <div className={styles.slot_row} onClick={handleButtonClick}>
      <div className={styles.wrapper_name}>
        <LogoOverlayWrapper size={20}>
          <LogoOverlay
            width={20}
            height={20}
            alt={selectedSlot?.name}
            image={selectedSlot?.logoUrl}
          />

          <Image
            src={selectedSlot?.imageUrl}
            width={20}
            height={20}
            alt={selectedSlot?.name}
          />
        </LogoOverlayWrapper>
        <span className={styles.slot_name}>{selectedSlot?.name}</span>
      </div>

      <SelectArrow isOpen={isOpen} />
    </div>
  );
};

export const SlotReviewModal = ({ isVisible, setVisible, slot }: Props) => {
  const alert = useAlert();
  const modalRef = useRef<HTMLDivElement | null>(null);

  const { data: me } = useMeQuery();

  const dropdownRef = useRef<DropdownRef | null>(null);

  const { data: { results: casinos = [] } = {} } = useCasinosBySlotQuery({
    id: slot?._id ?? '',
  });

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [submitReview, mutation] = useCreateSlotReviewMutation();
  const isLoading = mutation.isLoading;

  const onSuccess = useCallback(() => {
    alert.success('Review successfully added');
  }, [alert]);

  handleMutation({ ...mutation, onSuccess });

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const data = useMemo(() => {
    if (!searchQuery?.length) return casinos;

    return casinos?.filter((item) => {
      return crocodileSearch(item?.name, searchQuery);
    });
  }, [casinos, searchQuery]);

  const onSubmit = async (values: FormikValues) => {
    const { casino: casinoString, value, screenshot } = values;
    let casino = {} as ICasino;

    if (!me?.user?._id) {
      alert.error('Please login to leave a review');
      return;
    }

    if (casinoString) {
      casino = JSON.parse(casinoString);
    } else {
      alert.error('Please select slot');
      return;
    }

    if (Number(value) < 0 || Number(value) > 100) {
      alert.error('Please enter valid RTP value');
      return;
    }

    if (!screenshot) {
      alert.error('Please upload screenshot');
      return;
    }

    await submitReview({
      value,
      image: screenshot,
      slot_id: slot?._id!!,
      casino_id: casino?._id!!,
    });

    setVisible(false);
  };

  const handleCloseModal = () => {
    setVisible(false);
    setIsOpen(false);
    setSearchQuery('');
    dropdownRef.current?.close();
  };

  useClickOutside({ onClick: handleCloseModal, ref: modalRef });

  if (!isVisible) {
    return null;
  }

  return (
    <div className={clsx(styles.modalBody, styles.modalBodyActive)}>
      <div className={styles.container}>
        <Formik
          onSubmit={onSubmit}
          initialValues={initialValues}
          validationSchema={validationSchema}
        >
          {({
            dirty,
            values,
            isSubmitting,
            submitForm,
            validateForm,
            setFieldValue,
          }) => (
            <div className={styles.wrapper} ref={modalRef}>
              <div className={styles.top}>
                <div className={styles.topLeft}>
                  <div className={styles.text}>
                    <span>{'Add slot RTP review'}</span>
                  </div>
                </div>

                <div className={styles.closeButton} onClick={handleCloseModal}>
                  <Icon.Close />
                </div>
              </div>

              {casinos?.length > 0 && (
                <Dropdown
                  ref={dropdownRef}
                  button={
                    <DropDownButton
                      isOpen={isOpen}
                      onClick={handleToggle}
                      searchQuery={searchQuery}
                      setSearchQuery={setSearchQuery}
                      selectedSlot={
                        values?.casino
                          ? (JSON.parse(values?.casino) as ICasino)
                          : null
                      }
                    />
                  }
                >
                  {data?.map((item: ICasino) => (
                    <DropdownItem
                      key={item?._id}
                      onClick={() => {
                        setFieldValue('casino', JSON.stringify(item));
                        dropdownRef?.current?.close();
                        handleToggle();
                      }}
                    >
                      <div className={styles.slot_row}>
                        <div className={styles.wrapper_name}>
                          <LogoOverlayWrapper size={20}>
                            <LogoOverlay
                              width={20}
                              height={20}
                              alt={item?.name}
                              image={item?.logoUrl}
                            />
                            <Image
                              src={item?.imageUrl}
                              width={20}
                              height={20}
                              alt={item.name}
                            />
                          </LogoOverlayWrapper>
                          <span className={styles.slot_name}>{item.name}</span>
                        </div>
                      </div>
                    </DropdownItem>
                  ))}
                </Dropdown>
              )}

              <div className={styles.wrapperInput}>
                <InputField {...model.value} />
              </div>
              <div className={styles.screenshotInput}>
                <InputField {...model.screenshot} />
                <div className={styles.wrapperScreenshotButton}>
                  <Icon.Cloud />
                  <span>
                    {(values?.screenshot as any)?.name ?? 'Screenshot proof...'}
                  </span>
                </div>
              </div>
              <div className={styles.submitButton}>
                <Button
                  onClick={() => {
                    validateForm();
                    submitForm();
                  }}
                  disabled={!dirty || isSubmitting}
                  text={isLoading ? 'Loading...' : 'Send review'}
                />
              </div>
            </div>
          )}
        </Formik>
      </div>
    </div>
  );
};
